import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnInit, ViewEncapsulation, effect, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { SyncStatusDotComponent, SyncDotInfo } from 'src/shared/components/sync-status-dot/sync-status-dot.component';

import { PlatformPillsComponent } from './platform-pills.component';
import {
  BOOKING_ADVANCE_MONTHS,
  RentalTypesState,
  STAY_PRESETS,
  StayType,
  minStayWarning,
  numOrNull,
  stayLengthError,
  toIsoDate,
  validateWindows,
} from './booking-settings.model';

interface RentalWindow {
  id: string;
  // Each window owns one FormGroup: { name, start, end, minNights, maxNights }.
  // The user labels the window inline as a tidy-input alongside the two
  // datepickers and its own min/max-nights override.
  form: FormGroup;
}

@Component({
  selector: 'rental-types-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TidyUiComponentsModule, SyncStatusDotComponent, PlatformPillsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="rt">
      <!-- Sub-card 1: Rental Type -->
      <div class="rt-section" [formGroup]="limitsForm">
        <div class="rt__head">
          <span class="rt__title-line">
            <tidy-text [body]="'Rental Type'" class="rt__title bold" />
            <sync-status-dot [sync]="staySync()" />
          </span>
          <tidy-text
            [helper]="'Pick a rental type to set the nights guests can book, or choose Custom to enter your own limits.'"
            class="rt__sub" />
        </div>

        <!-- Stay type toggle (left) drives the Min/Max nights (right). Presets
             lock the inputs; Custom unlocks them. -->
        <div class="rt-stay">
          <div class="rt-stay__type">
            <tidy-text [helper]="'Stay type'" class="rt-stay__type-label" />
            <div class="rt-seg-group">
              @for (t of stayTypes; track t.value) {
                <button
                  type="button"
                  class="rt-seg"
                  [class.rt-seg--on]="stayType === t.value"
                  (click)="selectStayType(t.value)">
                  <tidy-text [body]="t.label" />
                </button>
              }
            </div>
          </div>

          <div class="rt-stay__nights">
            <tidy-input
              [label]="'Min nights'"
              [type]="'number'"
              class="rt-nights-input"
              [class.rt-nights-input--locked]="stayType !== 'custom'"
              [form]="limitsForm.controls.minNights"
              formControlName="minNights" />
            <div class="rt-nights-field">
              <tidy-input
                [label]="'Max nights'"
                [type]="'number'"
                class="rt-nights-input"
                [class.rt-nights-input--locked]="stayType !== 'custom'"
                [form]="limitsForm.controls.maxNights"
                formControlName="maxNights" />
              <platform-pills [key]="'max_stay'" />
            </div>
          </div>
        </div>
        @if (stayError) {
          <tidy-text [helper]="stayError" class="rt-error" />
        } @else if (stayWarning) {
          <tidy-text [helper]="stayWarning" class="rt-warning" />
        }
      </div>

      <!-- Sub-card 2: How Far in Advance Guests Can Book -->
      <div class="rt-section" [formGroup]="limitsForm">
        <div class="rt-availability__head">
          <span class="rt__title-line">
            <tidy-text [body]="'How Far in Advance Guests Can Book'" class="rt-availability__title bold" />
            <sync-status-dot [sync]="bookingAdvanceSync()" />
            <platform-pills [key]="'max_booking_advance_months'" />
          </span>
          <tidy-text
            [helper]="'How far out guests can book. For example, 6 months means guests can book up to 6 months ahead.'"
            class="rt-availability__sub" />
        </div>
        <div class="rt-availability">
          <tidy-select
            [label]="'Months'"
            [items]="bookingAdvanceItems"
            [form]="limitsForm.controls.maxBookingAdvanceMonths"
            formControlName="maxBookingAdvanceMonths" />
        </div>
      </div>

      <!-- Sub-card 3: Date Overrides -->
      <div class="rt-section rt-windows">
        <div class="rt-windows__head">
          <div class="rt-windows__head-text">
            <span class="rt__title-line">
              <tidy-text [body]="'Date Overrides'" class="rt-windows__title bold" />
              <sync-status-dot [sync]="dateOverridesSync()" />
            </span>
            <tidy-text
              [helper]="'Override the night limits for specific dates — require longer stays in the off-season, or allow shorter stays over a peak weekend.'"
              class="rt-windows__sub" />
          </div>
          <tidy-text
            class="white-link rt-windows__add"
            [actionBody]="'+ Add Window'"
            (action)="addWindow()" />
        </div>

        <div class="rt-window-list">
          @for (w of windows; track w.id) {
            <div class="rt-window">
              <div [formGroup]="w.form" class="rt-window__main">
                <tidy-input
                  [label]="'Window Name'"
                  [form]="w.form.controls.name"
                  formControlName="name"
                  class="rt-window__name-input" />
                <div class="rt-window__fields">
                  <tidy-datepicker
                    [label]="'Start'"
                    [form]="w.form.controls.start"
                    formControlName="start" />
                  <tidy-datepicker
                    [label]="'End'"
                    [form]="w.form.controls.end"
                    formControlName="end" />
                  <tidy-input
                    [label]="'Min nights'"
                    [type]="'number'"
                    [form]="w.form.controls.minNights"
                    formControlName="minNights" />
                  <tidy-input
                    [label]="'Max nights'"
                    [type]="'number'"
                    [form]="w.form.controls.maxNights"
                    formControlName="maxNights" />
                </div>
                @if (windowErrorById[w.id]; as windowError) {
                  <tidy-text [helper]="windowError" class="rt-error" />
                }
              </div>
              <button type="button" class="rt-window__remove" aria-label="Remove window" (click)="removeWindow(w)">
                <span class="rt-window__remove-icon" aria-hidden="true"></span>
              </button>
            </div>
          }
          @if (windows.length === 0) {
            <tidy-text [helper]="'No date windows. The night limits above apply to every date.'" class="rt-empty" />
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .rt {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 4px;
      color: var(--tidy-text-primary);
    }
    /* Each section is its own bordered sub-card, matching the Channel card's
       "Instant book" (.bs-card) treatment. */
    .rt-section {
      border: 1px solid var(--tidy-border-light);
      border-radius: 10px;
      padding: 14px 16px;
      background: var(--tidy-bg-card);
    }
    .rt__head { margin-bottom: 14px; }
    .rt__head > tidy-text { display: block; }
    /* Keeps a sub-card title and its sync dot together on one line. */
    .rt__title-line { display: inline-flex; align-items: center; gap: 6px; }
    /* Card / section headers match the Channel card's "Instant book" label:
       a bold body (14px), not a large header. */
    .rt__title .body-text { font-size: 14px; font-weight: 700; color: var(--tidy-text-primary); }
    .rt__sub   .helper-text { font-size: 12px; color: var(--tidy-text-light); line-height: 1.5; max-width: 760px; }

    /* Stay type segmented toggle on the left, Min/Max nights on the right. */
    .rt-stay {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      align-items: flex-start;
    }
    .rt-stay__type { display: flex; flex-direction: column; gap: 6px; }
    .rt-stay__type-label .helper-text { font-size: 12px; color: var(--tidy-text-light); }
    .rt-seg-group {
      display: inline-flex;
      border: 1px solid var(--tidy-border-light);
      border-radius: 8px;
      overflow: hidden;
    }
    .rt-seg {
      padding: 8px 12px;
      background: transparent;
      border: none;
      border-right: 1px solid var(--tidy-border-light);
      cursor: pointer;
      .body-text { font-size: 12px !important; font-weight: 600; color: var(--tidy-text-light) !important; }
    }
    .rt-seg:last-child { border-right: none; }
    .rt-seg--on {
      background: color-mix(in srgb, var(--primary-button) 18%, transparent);
      .body-text { color: var(--primary-button) !important; }
    }

    .rt-stay__nights {
      display: flex;
      gap: 10px;
    }
    .rt-stay__nights > tidy-input,
    .rt-stay__nights > .rt-nights-field {
      flex: 1 1 0;
      min-width: 130px;
      max-width: 180px;
    }
    .rt-nights-field { display: flex; flex-direction: column; gap: 4px; }
    /* Locked (preset) inputs dim so it's clear they're driven by the toggle. */
    .rt-nights-input--locked { opacity: 0.6; }

    /* Availability window — its own titled sub-card below the stay type. */
    .rt-availability__head { margin-bottom: 10px; }
    .rt-availability__head > tidy-text { display: block; }
    .rt-availability__title .body-text { font-size: 14px; font-weight: 700; color: var(--tidy-text-primary); }
    .rt-availability__sub   .helper-text { font-size: 12px; color: var(--tidy-text-light); margin-top: 2px; line-height: 1.5; max-width: 760px; }
    .rt-availability { max-width: 220px; }

    /* windows sub-card — spacing between sub-cards is handled by .rt's gap. */
    .rt-windows__head {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 10px;
    }
    .rt-windows__head-text {
      flex: 1 1 auto;
      min-width: 0;
      > tidy-text { display: block; }
    }
    .rt-windows__title .body-text { font-size: 14px; font-weight: 700; color: var(--tidy-text-primary); }
    .rt-windows__sub   .helper-text { font-size: 12px; color: var(--tidy-text-light); margin-top: 2px; line-height: 1.5; }
    .rt-windows__add { white-space: nowrap; }

    .rt-window-list { display: flex; flex-direction: column; gap: 8px; }
    .rt-window {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 14px; border: 1px solid var(--tidy-border-light); border-radius: 10px;
      background: var(--tidy-bg-card);
    }
    .rt-window__main {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    /* Editable name input + the two datepickers and min/max overrides. */
    .rt-window__name-input { display: block; max-width: 280px; }
    .rt-window__fields {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .rt-window__fields tidy-datepicker,
    .rt-window__fields tidy-input {
      flex: 1 1 0;
      min-width: 140px;
      max-width: 200px;
    }

    /* Destructive remove button — a red trash icon pinned to the row's
       top-right (the row is align-items:flex-start). Using a CSS mask on a
       span so the icon takes the red color cleanly. Dimmed to 60% so it
       isn't visually loud; full opacity on hover for affordance. */
    .rt-window__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px; height: 28px;
      border-radius: 6px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 0;
      opacity: 0.6;
      transition: opacity 0.15s ease, background-color 0.15s ease;
    }
    .rt-window__remove:hover {
      opacity: 1;
      background: color-mix(in srgb, var(--red) 14%, transparent);
    }
    .rt-window__remove-icon {
      display: inline-block;
      width: 16px; height: 16px;
      background-color: var(--red);
      -webkit-mask-image: url('/assets/svg/trash-outline.svg');
              mask-image: url('/assets/svg/trash-outline.svg');
      -webkit-mask-size: contain;
              mask-size: contain;
      -webkit-mask-repeat: no-repeat;
              mask-repeat: no-repeat;
      -webkit-mask-position: center;
              mask-position: center;
    }

    .rt-empty {
      padding: 14px;
      border: 1px dashed var(--tidy-border-light); border-radius: 8px; text-align: center;
      display: block;
    }

    /* Why a value isn't being saved. Shown under the stay-length inputs and
       under the offending date window. */
    .rt-error {
      display: block;
      margin-top: 8px;
      .helper-inline { font-size: 12px; color: var(--red) !important; }
    }
    .rt-warning { display: block; margin-top: 8px; }
  `],
})
export class RentalTypesComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Controlled value from the parent. Null in demo mode → keep local defaults. */
  value = input<RentalTypesState | null>(null);
  valueChange = output<RentalTypesState>();

  // Channel-sync dots, computed by the parent from platform_sync. `stay` covers the
  // Min/Max nights (min_stay + max_stay); `bookingAdvance` covers the advance months.
  staySync = input<SyncDotInfo | null>(null);
  bookingAdvanceSync = input<SyncDotInfo | null>(null);
  dateOverridesSync = input<SyncDotInfo | null>(null);

  // True while applying an incoming `value` so the resulting form/state writes
  // don't echo back out as `valueChange` (which would trigger a spurious save).
  private applying = false;

  constructor() {
    // Apply the parent's value whenever it arrives/changes (real mode only).
    // Kept in the constructor for the injection context; the callback runs later
    // (during CD), after all fields and ngOnInit have initialized.
    effect(() => {
      const v = this.value();
      if (v) { this.applyState(v); }
    });
  }

  ngOnInit(): void {
    // Emit when the limits/availability change; per-window name/date/limit changes
    // are wired in subscribeWindow(). Done here (not the constructor) so the forms
    // are guaranteed initialized.
    this.limitsForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitChange());
    for (const w of this.windows) { this.subscribeWindow(w); }
    // Lock Min/Max for the initial (default 'short') state. Demo mode never gets
    // an incoming `value`, so this is the only place it's set there; real mode
    // re-sets it in applyState.
    this.setNightsDisabled(this.stayType !== 'custom');
    this.refreshErrors(this.getValue());
  }

  /**
   * Presets lock the Min/Max inputs; Custom frees them. We disable the actual
   * FormControls — the tidy-input CVA's setDisabledState mirrors that onto the
   * native input, so binding `[disableElement]` here would be overwritten by
   * Angular the moment the control's own (enabled) state is applied.
   */
  private setNightsDisabled(disabled: boolean): void {
    const opts = { emitEvent: false };
    const min = this.limitsForm.get('minNights')!;
    const max = this.limitsForm.get('maxNights')!;
    if (disabled) { min.disable(opts); max.disable(opts); }
    else { min.enable(opts); max.enable(opts); }
  }

  // Segmented stay-type toggle. Default 'short'; presets lock Min/Max, 'custom'
  // frees them.
  stayType: StayType = 'short';
  readonly stayTypes: Array<{ value: StayType; label: string }> = [
    { value: 'short', label: 'Short' },
    { value: 'mid', label: 'Mid' },
    { value: 'long', label: 'Long' },
    { value: 'custom', label: 'Custom' },
  ];

  // Booking-advance months + the single default Min/Max nights pair. Seeded to
  // the 'short' preset (demo mode keeps these locally since `value` stays null).
  readonly limitsForm: FormGroup = this.fb.group({
    maxBookingAdvanceMonths: ['12'],
    minNights: [STAY_PRESETS.short.min],
    maxNights: [STAY_PRESETS.short.max],
  });

  /**
   * The API accepts only these month counts, so this is a select rather than a
   * free number input — a stray value would 400 the whole atomic save. There is
   * no "No limit" option: the key is in the backend's SYNC_MATRIX, so it can be
   * updated but never deleted, and Airbnb has no unlimited window to push anyway.
   *
   * SelectModel values are strings, so the control holds a string and the
   * number/null conversion happens in getValue()/applyState().
   */
  readonly bookingAdvanceItems: SelectModel[] =
    BOOKING_ADVANCE_MONTHS.map(m => ({ value: String(m), viewValue: new TranslationPipe().transform(`${m} months`) }));

  // Validation messages, recomputed on every change rather than read through a
  // template getter (OnPush + the no-functions-in-templates rule).
  stayError: string | null = null;
  stayWarning: string | null = null;
  windowErrorById: Record<string, string> = {};

  /** Monotonic suffix for generated window ids (see addWindow). */
  private windowSeq = 0;

  // Each window owns one FormGroup with name + start + end + min/max nights so
  // all inputs bind independently. addWindow() seeds the same shape with a
  // default name and blank night overrides.
  windows: RentalWindow[] = [
    {
      id: 'w1',
      form: this.fb.group({
        name: ['Winter off-season'],
        start: ['2027-01-05'],
        end: ['2027-03-15'],
        minNights: [30],
        maxNights: [365],
      }),
    },
    {
      id: 'w2',
      form: this.fb.group({
        name: ['Coachella weekend'],
        start: ['2027-04-10'],
        end: ['2027-04-20'],
        minNights: [3],
        maxNights: [7],
      }),
    },
  ];

  /**
   * The customer's own min/max, kept while a preset is selected so returning to
   * Custom restores what they typed rather than stranding them on whichever
   * preset they looked at last.
   */
  private customNights: { minNights: unknown; maxNights: unknown } | null = null;

  /** Toggle a stay type: presets patch fixed min/max and lock; custom frees them. */
  selectStayType(type: StayType): void {
    if (this.stayType === 'custom' && type !== 'custom') {
      const { minNights, maxNights } = this.limitsForm.getRawValue();
      this.customNights = { minNights, maxNights };
    }
    this.stayType = type;
    // emitEvent:false — we emit once, explicitly, below (avoids a double emit).
    if (type !== 'custom') {
      const preset = STAY_PRESETS[type];
      this.limitsForm.patchValue({ minNights: preset.min, maxNights: preset.max }, { emitEvent: false });
    } else if (this.customNights) {
      this.limitsForm.patchValue(this.customNights, { emitEvent: false });
    }
    this.setNightsDisabled(type !== 'custom');
    this.emitChange();
  }

  removeWindow(w: RentalWindow): void {
    this.windows = this.windows.filter(x => x.id !== w.id);
    this.emitChange();
  }

  addWindow(): void {
    // Counter-suffixed so two windows added in the same millisecond can't collide
    // (the API requires unique ids).
    const id = `w-${Date.now()}-${this.windowSeq++}`;
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    const toIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const win: RentalWindow = {
      id,
      // Seeded with a real name: the API rejects a blank one, and because the
      // save is atomic that would block every other setting on the page from
      // saving until the user happened to type something.
      form: this.fb.group({
        name: [`Window ${this.windows.length + 1}`],
        start: [toIso(start)],
        end: [toIso(end)],
        minNights: [null],
        maxNights: [null],
      }),
    };
    this.subscribeWindow(win);
    this.windows = [...this.windows, win];
    this.emitChange();
  }

  // ---------- Controlled-value plumbing (save/load) ----------

  /** Snapshot the current state for the parent to persist. */
  getValue(): RentalTypesState {
    // getRawValue (not .value) so preset-locked Min/Max — which are disabled
    // FormControls — are still captured.
    const l = this.limitsForm.getRawValue();
    return {
      stayType: this.stayType,
      minNights: numOrNull(l.minNights),
      maxNights: numOrNull(l.maxNights),
      maxBookingAdvanceMonths: numOrNull(l.maxBookingAdvanceMonths),
      windows: this.windows.map(w => ({
        id: w.id,
        name: w.form.value.name ?? '',
        start: toIsoDate(w.form.value.start),
        end: toIsoDate(w.form.value.end),
        minNights: numOrNull(w.form.value.minNights),
        maxNights: numOrNull(w.form.value.maxNights),
      })),
    };
  }

  private applyState(state: RentalTypesState): void {
    this.applying = true;
    this.stayType = state.stayType ?? 'short';
    // For a preset, the nights ARE the preset (kept authoritative in code); only
    // 'custom' honors the stored values.
    const min = this.stayType === 'custom' ? state.minNights : STAY_PRESETS[this.stayType].min;
    const max = this.stayType === 'custom' ? state.maxNights : STAY_PRESETS[this.stayType].max;
    // Seed the remembered custom values from a saved custom strategy, so
    // toggling to a preset and back restores it rather than the preset.
    this.customNights = this.stayType === 'custom'
      ? { minNights: state.minNights, maxNights: state.maxNights }
      : null;
    this.limitsForm.patchValue(
      {
        // '' leaves the select unfilled for an address with no stored row.
        maxBookingAdvanceMonths: state.maxBookingAdvanceMonths === null ? '' : String(state.maxBookingAdvanceMonths),
        minNights: min,
        maxNights: max,
      },
      { emitEvent: false },
    );
    this.setNightsDisabled(this.stayType !== 'custom');
    // Rebuild windows with fresh FormGroups so OnPush `@for track id` re-renders.
    this.windows = state.windows.map(w => ({
      id: w.id,
      form: this.fb.group({ name: [w.name], start: [w.start], end: [w.end], minNights: [w.minNights], maxNights: [w.maxNights] }),
    }));
    for (const w of this.windows) { this.subscribeWindow(w); }
    this.applying = false;
    this.refreshErrors(this.getValue());
  }

  private emitChange(): void {
    if (this.applying) { return; }
    const value = this.getValue();
    this.refreshErrors(value);
    this.valueChange.emit(value);
  }

  /**
   * Mirrors the server-side rules. `toEntries` drops the whole stay-length group
   * when any of this fails, so without these messages the card would just
   * silently stop persisting.
   */
  private refreshErrors(value: RentalTypesState): void {
    this.stayError = stayLengthError(value);
    this.stayWarning = minStayWarning(value);
    this.windowErrorById = Object.fromEntries(
      validateWindows(value.windows).map(e => [e.id, e.message]),
    );
    this.cdr.markForCheck();
  }

  private subscribeWindow(w: RentalWindow): void {
    w.form.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitChange());
  }
}
